import type { ThreatBan, ThreatDetail, ThreatExclusion, ThreatHistory, ThreatIP, ThreatObservation, ThreatSource } from '@workspace/silk-ui';

export class ThreatAPIError extends Error {
  constructor(message: string, public status: number) { super(message); }
}
export function createThreatAPI(tenant: string, signal: AbortSignal, transport: typeof fetch = fetch) {
  async function request<T>(path: string, method = 'GET', body?: unknown, text = false): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Expected-Tenant-ID': tenant };
    if (method !== 'GET') {
      const cookie = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith('csrf='));
      if (cookie) { try { headers['X-CSRF-Token'] = decodeURIComponent(cookie.slice(5)); } catch { /* Server rejects missing proof. */ } }
    }
    const response = await transport(`/api/v1/threat-protection/${path}`, {
      method, credentials: 'include', cache: 'no-store', headers, signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { detail?: string; error?: string };
      throw new ThreatAPIError(payload.detail || payload.error || `Request failed (${response.status})`, response.status);
    }
    if (text) return await response.text() as T;
    return ((await response.json()) as { data: T }).data;
  }
  const ipPath = (ip: string) => `ips/${encodeURIComponent(ip)}`;
  return {
    ips: (cursor = '') => request<ThreatIP[]>(`ips?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`),
    bans: () => request<ThreatBan[]>('bans'),
    sources: () => request<ThreatSource[]>('sources'),
    allowlist: () => request<ThreatExclusion[]>('allowlist'),
    async detail(ip: string): Promise<ThreatDetail> {
      const [reputation, observations, history] = await Promise.all([
        request<ThreatIP>(ipPath(ip)), request<ThreatObservation[]>(`${ipPath(ip)}/observations`), request<ThreatHistory[]>(`${ipPath(ip)}/history`),
      ]);
      return { reputation, observations, history };
    },
    ban: (ip: string, duration: string, reason: string) => request<ThreatIP>('bans', 'POST', { ip, duration, reason }),
    unban: (ip: string, duration: string, reason: string) => request<ThreatIP>(`bans/${encodeURIComponent(ip)}`, 'DELETE', { duration, reason }),
    exclusion: (network: string, reason: string, remove = false) => request<{ updated: boolean }>('allowlist', remove ? 'DELETE' : 'POST', { network, reason }),
    checkExclusion: (ip: string) => request<{ ip: string; reason: string }>(`${ipPath(ip)}/exclusion`),
    recalculate: (ip: string) => request<ThreatIP>(`${ipPath(ip)}/recalculate`, 'POST'),
    sync: (id: string) => request<{ source_id: string }>(`sources/${encodeURIComponent(id)}/sync`, 'POST'),
    exportText: () => request<string>('bans.txt', 'GET', undefined, true),
  };
}
export type ThreatAPI = ReturnType<typeof createThreatAPI>;

// Strict literals only; URL parsing is used for IPv6 syntax, never for networking.
export function validIP(value: string): boolean {
  if (value.includes(':')) {
    if (!/^[0-9a-fA-F:.]+$/.test(value)) return false;
    try { return new URL(`http://[${value}]/`).hostname.startsWith('['); } catch { return false; }
  }
  return /^(0|[1-9]\d{0,2})(\.(0|[1-9]\d{0,2})){3}$/.test(value) && value.split('.').every(part => Number(part) <= 255);
}
export function validNetwork(value: string): boolean {
  const [ip, prefix, extra] = value.split('/');
  return validIP(ip) && extra === undefined && (prefix === undefined || (/^(0|[1-9]\d*)$/.test(prefix) && Number(prefix) <= (ip.includes(':') ? 128 : 32)));
}
